import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IAppState } from 'shared/common/state';
import { getOfferCardCreator, integrateAd } from 'shared/containers/OffersContainer/helpers';
import { useOffersCallbacks } from 'shared/containers/OffersContainer/useOffersCallbacks';
import { useOfferProps } from 'shared/hooks/useOfferProps';
import { IOffer } from 'shared/offer';
import { selectSource } from 'shared/selectors';
import { selectServicesBannerPositions } from 'shared/selectors/selectServicesBannerPositions';
import { useApplicationContext } from 'shared/utils/applicationContext';

type TRendererProps = {
  pageNumber: number;
  offers: IOffer[];
  suggestions?: IOffer[];
  offset?: number;
  keyPrefix?: string;
};

export function useSuggestionsOffersRenderer(): (props: TRendererProps) => React.ReactNode[] {
  const { config } = useApplicationContext();

  const offerProps = useOfferProps();
  const offerCallbackProps = useOffersCallbacks({ userIsAuthenticated: offerProps.user.isAuthenticated });

  const offerCardCreatorProps = useMemo(
    () => ({
      ...offerProps,
      ...offerCallbackProps,
    }),
    [offerProps, offerCallbackProps],
  );

  const suggestionsDistances = useSelector((state: IAppState) => state.suggestionsDistances);
  const jsonQuery = useSelector((state: IAppState) => state.results.jsonQuery);
  const source = useSelector(selectSource);
  const mlRankingModelVersion = useSelector((state: IAppState) => state.mlRankingModelVersion);
  const servicesBannerPositions = useSelector(selectServicesBannerPositions);

  const getSuggestionsWithDistances = useCallback(
    (suggestions: IOffer[]): IOffer[] => {
      if (suggestionsDistances !== undefined && suggestions.length > 0) {
        return suggestions.map(suggest => {
          const distaces =
            suggestionsDistances?.data && suggestionsDistances?.data.find(distance => distance.offerId === suggest.id);

          return distaces ? { ...suggest, suggestionDistances: distaces.distance } : suggest;
        });
      }

      return suggestions;
    },
    [suggestionsDistances],
  );

  return useCallback(
    ({ pageNumber, offers, suggestions, offset = 0, keyPrefix = 'suggestions' }) =>
      integrateAd({
        suggestionFragment: {
          isSimilar: true,
          startIndex: 0,
        },
        mlRankingModelVersion,
        servicesBannerPositions,
        jsonQuery,
        currentPageNumber: pageNumber,
        perPage: Number(config.get('serp.offersPerPage')),
        offers: getSuggestionsWithDistances(offers),
        offerCardCreator: getOfferCardCreator({
          props: offerCardCreatorProps,
          isSimilar: true,
          isSimilarFromML: true,
          pageNum: pageNumber,
          source,
        }),
        isPrintEnabled: offerCardCreatorProps.isPrintEnabled,
        // меняем знак, чтобы не рефачить integrateAd
        // в котором extendedOffersOffset вычитается из позиции расчитаной по номеру страницы
        // нужно чтобы корректно сдвинуть 1 страницу на сдвиг добивок
        extendedOffersOffset: -offset,
        suggestions,
        hiddenOffers: offerCardCreatorProps.hideOffer.hiddenOffers,
        hiddenSuggestions: offerCardCreatorProps.hideOffer.hiddenSuggestions,
        mlRankingGuid: offerCardCreatorProps.mlRankingGuid,
        keyPrefix,
      }),
    [
      getSuggestionsWithDistances,
      jsonQuery,
      mlRankingModelVersion,
      offerCardCreatorProps,
      servicesBannerPositions,
      source,
      config,
    ],
  );
}
